import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useUserAppsContext } from './user-apps-context';

const NavigationModeContext = createContext();

/**
 * Provides navigation mode state ("hover" | "xy_cursor") across the application.
 * The mode is loaded from user settings and persists across page refreshes.
 * Also manages active state for XY cursor navigation (similar to hover navigation).
 */
export function NavigationModeProvider({ children }) {
    const { userSettings, updateSettings } = useUserAppsContext();
    const [navigationMode, setNavigationModeState] = useState('hover');
    const [xyCursorActive, setXyCursorActive] = useState(false);

    // Load navigation mode from user settings on mount
    useEffect(() => {
        if (userSettings?.navigation_mode) {
            setNavigationModeState(userSettings.navigation_mode);
        }
    }, [userSettings?.navigation_mode]);

    // Update both local state and persist to backend
    const setNavigationMode = useCallback(async (mode) => {
        if (mode !== 'hover' && mode !== 'xy_cursor') {
            console.error('Invalid navigation mode:', mode);
            return;
        }

        // Optimistic update
        setNavigationModeState(mode);

        // Persist to backend
        await updateSettings({ navigation_mode: mode });
    }, [updateSettings]);

    // Toggle XY cursor on/off
    const toggleXyCursor = useCallback(() => {
        setXyCursorActive((prev) => !prev);
    }, []);

    const value = useMemo(() => ({
        navigationMode,
        setNavigationMode,
        xyCursorActive,
        toggleXyCursor,
    }), [navigationMode, setNavigationMode, xyCursorActive, toggleXyCursor]);

    return (
        <NavigationModeContext.Provider value={value}>
            {children}
        </NavigationModeContext.Provider>
    );
}

export function useNavigationMode() {
    const context = useContext(NavigationModeContext);
    if (!context) {
        throw new Error('useNavigationMode must be used within NavigationModeProvider');
    }
    return context;
}
